<template>
  <a-modal
    :visible="visible"
    :title="modalTitle"
    @cancel="handleCancel"
    :footer="null"
    width="700px"
  >
    <div class="daily-rate-content">
      <div class="info-section">
        <div class="info-row">
          <div class="info-label">병원명:</div>
          <div class="info-value">{{ subscription.hospitalName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">상품명:</div>
          <div class="info-value">{{ subscription.productName }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">계약 시작일:</div>
          <div class="info-value">{{ formatDate(subscription.contractStartDate) }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">월 기준 요금:</div>
          <div class="info-value">{{ formatAmount(subscription.priceIncludingVAT) }}</div>
        </div>
        <div class="info-row">
          <div class="info-label">일일 기준 요금:</div>
          <div class="info-value">{{ formatAmount(dailyRate) }}</div>
        </div>
      </div>

      <div class="table-section">
        <h3>일할 계산 상세 내역</h3>
        <p class="description">
          계약 시작일({{ formatDate(subscription.contractStartDate) }})부터 해당 월 말일({{ formatDate(monthEndDate) }})까지의 일할 계산 내역입니다.
        </p>
        
        <a-table
          :columns="columns"
          :data-source="dailyRateData"
          :pagination="false"
          size="small"
          bordered
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'amount'">
              {{ formatAmount(text) }}
            </template>
          </template>
        </a-table>
        
        <div class="total-section">
          <div class="total-row">
            <div class="total-label">총 일수:</div>
            <div class="total-value">{{ totalDays }}일</div>
          </div>
          <div class="total-row">
            <div class="total-label">총 금액:</div>
            <div class="total-value strong">{{ formatAmount(totalAmount) }}</div>
          </div>
        </div>
      </div>
      
      <div class="action-section">
        <a-button @click="handleCancel">닫기</a-button>
        <a-button type="primary" class="yellow-button" @click="handlePrint">인쇄</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';

export default {
  name: 'DailyRateModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subscription: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:visible', 'close'],
  setup(props, { emit }) {
    // 모달 제목
    const modalTitle = computed(() => {
      return `일할 계산 - ${props.subscription.hospitalName || ''}`;
    });
    
    // 계약 시작일 기준 월 말일 계산
    const monthEndDate = computed(() => {
      if (!props.subscription.contractStartDate) return null;
      
      const startDate = dayjs(props.subscription.contractStartDate);
      return startDate.endOf('month').valueOf();
    });
    
    // 해당 월의 총 일수 계산
    const daysInMonth = computed(() => {
      if (!props.subscription.contractStartDate) return 30;
      
      const startDate = dayjs(props.subscription.contractStartDate);
      return startDate.daysInMonth();
    });
    
    // 일일 요금 계산
    const dailyRate = computed(() => {
      if (!props.subscription.priceIncludingVAT) return 0;
      
      return Math.round(props.subscription.priceIncludingVAT / daysInMonth.value);
    });
    
    // 계약 시작일부터 월 말일까지의 일수 계산
    const totalDays = computed(() => {
      if (!props.subscription.contractStartDate || !monthEndDate.value) return 0;
      
      const startDate = dayjs(props.subscription.contractStartDate);
      const endDate = dayjs(monthEndDate.value);
      
      // 시작일과 종료일을A 포함하여 일수 계산
      return endDate.diff(startDate, 'day') + 1;
    });
    
    // 총 금액 계산
    const totalAmount = computed(() => {
      return dailyRate.value * totalDays.value;
    });
    
    // 일별 데이터 생성
    const dailyRateData = computed(() => {
      if (!props.subscription.contractStartDate || !monthEndDate.value) return [];
      
      const result = [];
      const startDate = dayjs(props.subscription.contractStartDate);
      const endDate = dayjs(monthEndDate.value);
      
      let currentDate = startDate;
      let index = 1;
      
      while (currentDate.isSame(endDate) || currentDate.isBefore(endDate)) {
        result.push({
          key: index,
          date: currentDate.valueOf(),
          day: currentDate.format('YYYY-MM-DD (ddd)'),
          amount: dailyRate.value
        });
        
        currentDate = currentDate.add(1, 'day');
        index++;
      }
      
      return result;
    });
    
    // 테이블 컬럼 정의
    const columns = [
      {
        title: '번호',
        dataIndex: 'key',
        key: 'key',
        width: 70,
        align: 'center'
      },
      {
        title: '날짜',
        dataIndex: 'day',
        key: 'day',
        align: 'center'
      },
      {
        title: '일할 금액',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        width: 150
      }
    ];
    
    // 날짜 포맷팅 함수
    const formatDate = (timestamp) => {
      if (!timestamp) return '-';
      return dayjs(timestamp).format('YYYY-MM-DD');
    };
    
    // 금액 포맷팅 함수
    const formatAmount = (amount) => {
      if (amount === undefined || amount === null) return '-';
      return amount.toLocaleString() + '원';
    };
    
    // 모달 닫기 핸들러
    const handleCancel = () => {
      emit('update:visible', false);
      emit('close');
    };
    
    // 인쇄 핸들러
    const handlePrint = () => {
      window.print();
    };
    
    return {
      modalTitle,
      monthEndDate,
      dailyRate,
      totalDays,
      totalAmount,
      dailyRateData,
      columns,
      formatDate,
      formatAmount,
      handleCancel,
      handlePrint
    };
  }
};
</script>

<style scoped lang="scss">
.daily-rate-content {
  padding: 20px;
}

.info-section {
  margin-bottom: 20px;
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.info-label {
  font-weight: 500;
  min-width: 100px;
}

.info-value {
  flex: 1;
}

.table-section {
  margin-bottom: 20px;
  
  h3 {
    font-size: 16px;
    margin-bottom: 10px;
  }
  
  .description {
    color: #666;
    font-size: 14px;
    margin-bottom: 15px;
  }
}

.total-section {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.total-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.total-label {
  font-weight: 500;
  margin-right: 10px;
}

.total-value {
  min-width: 120px;
  text-align: right;
  
  &.strong {
    font-weight: bold;
    font-size: 16px;
    color: #1890ff;
  }
}

.action-section {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 인쇄 스타일
@media print {
  .action-section {
    display: none;
  }
}
</style>
